import {
  FETCH_MECHANISM,
  GrapplerLogger,
  SENDBEACON_MECHANISM,
} from 'saddlebag-grappler';
import { logError, logOperationalEvent } from 'saddlebag-logger';
import observer from 'saddlebag-observer';
import { isNonEssentialTrackingEnabled } from 'saddlebag-tracking-preferences';
import { MiniEventsTracking } from 'saddlebag-user-tracking-events';

const grapplerTrackers = new Map();
const miniEventTrackers = new Map();
let initializedListeners = false;

const getMiniEventTracker = (environment, componentName, async) => {
  const env = environment || 'public';
  const mechanism = async ? SENDBEACON_MECHANISM : FETCH_MECHANISM;
  const key = `${env}:${componentName}:${mechanism}`;
  if (!miniEventTrackers.has(key)) {
    miniEventTrackers.set(
      key,
      new MiniEventsTracking(env, componentName, {
        enhance: false,
        mechanism,
      }),
    );
  }
  return miniEventTrackers.get(key);
};

const getGrapplerTracker = (environment, componentName, async) => {
  const env = environment || 'public';
  const mechanism = async ? SENDBEACON_MECHANISM : FETCH_MECHANISM;
  const key = `${env}:${componentName}:${mechanism}`;
  if (!grapplerTrackers.has(key)) {
    grapplerTrackers.set(
      key,
      new GrapplerLogger(env, componentName, {
        enhance: false,
        mechanism,
      }),
    );
  }
  return grapplerTrackers.get(key);
};

const sendGrapplerEvent = async (
  environment,
  componentName,
  data,
  async = false,
) => {
  try {
    const eventComponentName = data.grapplerServiceName || componentName;
    if (data.isMiniEvent === true) {
      return getMiniEventTracker(environment, eventComponentName, async).track(
        data.eventName,
        data.fullSchemaName,
        data.message,
        data.messageOpts,
      );
    }
    return getGrapplerTracker(environment, eventComponentName, async).sendEvent(
      data.eventName,
      data.fullSchemaName,
      data.message,
    );
  } catch (err) {
    logError(err, {
      libraryName: 'trackolding',
      component: data?.grapplerServiceName || componentName,
      eventType: data?.eventName,
    });
    return {};
  }
};

/**
 * @param {string} environment environment, public or public-sandbox
 * @param {string} componentName component name — the page-level default,
 * itself resolved from defaultGrapplerServiceName in the page init data if
 * provided (see grappler.js), else microsite.name.
 * @param {string} market Market of operation
 * @param {object} data Object containing the event to be sent to grappler (message),
 * type of the event (isMiniEvent) and if consent is required (requiredConsent)
 * @param {string} [data.grapplerServiceName] Optional per-event override. If
 * set, routes this specific event to this service name instead of
 * componentName. Takes precedence over both the page-level
 * defaultGrapplerServiceName and microsite.name.
 * @returns {*} Returns nothing
 */
const trackGrapplerEvent = async (
  environment,
  componentName,
  market,
  data,
  async = false,
) => {
  if (data && data.eventName) {
    if (data.consentRequired !== false) {
      if (!isNonEssentialTrackingEnabled(market)) {
        return {};
      }
    }
    return sendGrapplerEvent(environment, componentName, data, async);
  }
  logOperationalEvent({
    eventName: 'GrapplerTrack_missingEventName',
    message: `Failed to track event as eventName not provided, market: ${JSON.stringify(
      market,
    )}, data: ${JSON.stringify(data)}`,
    libraryName: 'trackolding',
    component: componentName,
  });
  return {};
};

/**
 * @returns {*} Returns nothing
 * @param  {string} environment environment, public or public-sandbox
 * @param  {string} componentName component name
 * @param {string} market Market of operation
 */
const initGrapplerListeners = (environment, componentName, market) => {
  if (initializedListeners) return;
  initializedListeners = true;
  observer.subscribe('grappler-track', (data) => {
    if (componentName && data && market && data.isMiniEvent != null) {
      return trackGrapplerEvent(
        environment,
        componentName,
        market,
        data,
        false,
      );
    }
    logOperationalEvent({
      eventName: 'grapplerTrack_invalidParameters',
      message: 'Failed to track grappler events. Invalid input parameters.',
      libraryName: 'trackolding',
      component: componentName,
    });
    return {};
  });
  observer.subscribe('grappler-track-async', (data) => {
    if (componentName && data && market && data.isMiniEvent != null) {
      return trackGrapplerEvent(environment, componentName, market, data, true);
    }
    logOperationalEvent({
      eventName: 'grapplerTrack_invalidParameters',
      message: 'Failed to track grappler events. Invalid input parameters.',
      libraryName: 'trackolding',
      component: componentName,
    });
    return {};
  });
};

export { initGrapplerListeners, sendGrapplerEvent, trackGrapplerEvent };
